import * as Yup from "yup";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
// @mui
import { LoadingButton } from "@mui/lab";
import {
  Container,
  Grid,
  Card,
  Stack,
  Typography,
  Box,
  Alert,
  TextField,
  CircularProgress,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
// components
import { useSettingsContext } from "../components/settings";
import { useSnackbar } from "../components/snackbar";
import FormProvider, { RHFTextField } from "../components/hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { safeBackfillUserRequest } from "../controllers";
import axios from "axios";

// ----------------------------------------------------------------------

interface FormValues {
  sourceUserId: string;
  destinationUserId: string;
}

interface MismatchWarning {
  sourceName: string;
  destinationName: string;
}

const SAFETY_CRITERIA = [
  {
    title: "No existing meal log entries",
    description: "The destination user must not have any meal log entries",
  },
  {
    title: "No existing workout entries",
    description: "The destination user must not have any workout entries",
  },
  {
    title: "Account age under one week",
    description:
      "The destination user account must have been created within the last 7 days",
  },
];

export default function SafeBackfill() {
  const { themeStretch } = useSettingsContext();
  const { enqueueSnackbar } = useSnackbar();

  const [mismatchWarning, setMismatchWarning] = useState<MismatchWarning | null>(null);
  const [confirmationText, setConfirmationText] = useState("");
  const [pendingFormData, setPendingFormData] = useState<FormValues | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const defaultValues: FormValues = {
    sourceUserId: "",
    destinationUserId: "",
  };

  const SafeBackfillSchema = Yup.object().shape({
    sourceUserId: Yup.string().required("sourceUserId is required"),
    destinationUserId: Yup.string().required("destinationUserId is required"),
  });

  const methods = useForm<FormValues>({
    resolver: yupResolver(SafeBackfillSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isValid },
  } = methods;

  const performBackfill = async (data: FormValues, confirmMismatch?: string) => {
    setIsLoading(true);
    try {
      const response = await safeBackfillUserRequest({
        sourceUserId: data.sourceUserId,
        destinationUserId: data.destinationUserId,
        confirmMismatch,
      });

      if (response.requiresConfirmation) {
        // Show the mismatch warning
        setMismatchWarning({
          sourceName: response.sourceName || "Unknown",
          destinationName: response.destinationName || "Unknown",
        });
        setPendingFormData(data);
        return;
      }

      // Success
      reset();
      setMismatchWarning(null);
      setConfirmationText("");
      setPendingFormData(null);
      enqueueSnackbar(
        `Successfully backfilled user data: ${data.sourceUserId} to ${data.destinationUserId}`
      );
    } catch (error) {
      console.error(error);
      let errorMessage = `Could not backfill user data: ${data.sourceUserId} to ${data.destinationUserId}`;

      if (axios.isAxiosError(error) && error.response?.data) {
        const responseData = error.response.data;
        if (typeof responseData === "string") {
          errorMessage = responseData;
        } else {
          errorMessage =
            responseData.data?.message ||
            responseData.message ||
            responseData.error ||
            errorMessage;
        }
      }

      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data: FormValues) => {
    await performBackfill(data);
  };

  const handleConfirmMismatch = async () => {
    if (pendingFormData && confirmationText === "Proceed") {
      await performBackfill(pendingFormData, "Proceed");
    }
  };

  const handleCancelMismatch = () => {
    setMismatchWarning(null);
    setConfirmationText("");
    setPendingFormData(null);
  };

  const isConfirmationValid = confirmationText === "Proceed";

  return (
    <>
      <Helmet>
        <title>Safe Backfill User Data | Admin Portal</title>
      </Helmet>

      <Container maxWidth={themeStretch ? false : "xl"}>
        <Typography variant="h3" component="h1" paragraph>
          Safe Backfill User Data
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card sx={{ p: 3, mb: 3, bgcolor: "action.hover" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Safety Requirements
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2.5 }}
              >
                This tool will only proceed if the destination user meets all of
                the following criteria:
              </Typography>
              <Stack spacing={1.5}>
                {SAFETY_CRITERIA.map((criterion, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        mt: 0.25,
                      }}
                    >
                      <CheckIcon sx={{ fontSize: 14, color: "white" }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {criterion.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {criterion.description}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Card>

            {isLoading && (
              <Alert
                severity="info"
                icon={<CircularProgress size={20} />}
                sx={{ mb: 3 }}
              >
                <Typography variant="body2">
                  Processing backfill request... This may take a moment.
                </Typography>
              </Alert>
            )}

            {mismatchWarning && (
              <Alert
                severity="warning"
                icon={<WarningAmberIcon />}
                sx={{ mb: 3 }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Name Mismatch Detected
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  The names on these accounts do not match:
                </Typography>
                <Box sx={{ mb: 2, pl: 2 }}>
                  <Typography variant="body2">
                    <strong>Source:</strong> {mismatchWarning.sourceName}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Destination:</strong> {mismatchWarning.destinationName}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  To proceed, type <strong>Proceed</strong> below and click confirm.
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    size="small"
                    placeholder='Type "Proceed" to confirm'
                    value={confirmationText}
                    onChange={(e) => setConfirmationText(e.target.value)}
                    sx={{ bgcolor: "background.paper" }}
                  />
                  <Stack direction="row" spacing={1}>
                    <LoadingButton
                      variant="contained"
                      color="warning"
                      onClick={handleConfirmMismatch}
                      disabled={!isConfirmationValid || isLoading}
                      loading={isLoading}
                    >
                      Confirm Backfill
                    </LoadingButton>
                    <LoadingButton
                      variant="outlined"
                      onClick={handleCancelMismatch}
                      disabled={isLoading}
                    >
                      Cancel
                    </LoadingButton>
                  </Stack>
                </Stack>
              </Alert>
            )}

            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
              <Card sx={{ p: 3 }}>
                <Stack spacing={3}>
                  <RHFTextField
                    name="sourceUserId"
                    label="Source userId"
                    required
                    disabled={!!mismatchWarning || isLoading}
                  />
                  <RHFTextField
                    name="destinationUserId"
                    label="Destination userId"
                    required
                    disabled={!!mismatchWarning || isLoading}
                  />
                </Stack>
              </Card>
              <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
                <LoadingButton
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  loading={isLoading}
                  disabled={!isValid || !!mismatchWarning || isLoading}
                >
                  Safe Backfill
                </LoadingButton>
              </Stack>
            </FormProvider>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}
